import { useMemo } from "react";
import { alpha, createTheme } from "@mui/material";

const cyan = "#43D9E7";
const red = "#FF4D57";
const ink = "#06090F";

export default function useArlTheme() {
  return useMemo(() =>
    createTheme({
      palette: {
        mode: "dark",
        primary: { main: cyan, light: "#86F3FC", dark: "#08AAB8", contrastText: ink },
        secondary: { main: red, light: "#FF858B", dark: "#CE2430", contrastText: "#FFFFFF" },
        background: { default: ink, paper: "#0D121B" },
        divider: "rgba(255,255,255,.09)",
        success: { main: "#43D98B" },
        warning: { main: "#FFCA55" },
        text: { primary: "#F5F7FA", secondary: "#9DA8B7" },
      },
      shape: { borderRadius: 10 },
      spacing: 8,
      typography: {
        fontFamily: '"Inter", "Arial Narrow", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        h1: { fontWeight: 900, letterSpacing: "-.045em", lineHeight: .92 },
        h2: { fontWeight: 900, letterSpacing: "-.04em", lineHeight: .98 },
        h3: { fontWeight: 850, letterSpacing: "-.035em", lineHeight: 1.02 },
        h4: { fontWeight: 850, letterSpacing: "-.03em", lineHeight: 1.08 },
        h5: { fontWeight: 800, letterSpacing: "-.02em" },
        h6: { fontWeight: 800, letterSpacing: "-.015em" },
        body1: { lineHeight: 1.7 },
        body2: { lineHeight: 1.65 },
        overline: { fontWeight: 800, letterSpacing: ".16em", lineHeight: 1.6 },
        button: { fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase" },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: { scrollBehavior: "smooth", colorScheme: "dark" },
            body: {
              backgroundColor: ink,
              backgroundImage: "radial-gradient(circle at 15% -10%, rgba(67,217,231,.10), transparent 28rem), radial-gradient(circle at 90% 15%, rgba(255,77,87,.07), transparent 25rem)",
              backgroundAttachment: "fixed",
            },
            "::selection": { background: alpha(cyan, .28), color: "#fff" },
            "*:focus-visible": { outline: `2px solid ${cyan}`, outlineOffset: 3 },
          },
        },
        MuiContainer: {
          defaultProps: { maxWidth: "lg" },
          styleOverrides: { root: { paddingInline: 20, "@media (min-width:600px)": { paddingInline: 32 } } },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              background: "rgba(6,9,15,.82)",
              backdropFilter: "blur(18px) saturate(140%)",
              borderBottom: "1px solid rgba(255,255,255,.08)",
              boxShadow: "none",
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "linear-gradient(145deg, rgba(255,255,255,.025), transparent 38%)",
              border: "1px solid rgba(255,255,255,.08)",
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: "#0D121B",
              backgroundImage: "linear-gradient(145deg, rgba(255,255,255,.035), transparent 45%)",
              border: "1px solid rgba(255,255,255,.09)",
              boxShadow: "0 18px 50px rgba(0,0,0,.22)",
              transition: "transform .25s ease, border-color .25s ease, box-shadow .25s ease",
              "@media (hover:hover)": { "&:hover": { transform: "translateY(-4px)", borderColor: alpha(cyan, .45), boxShadow: "0 24px 65px rgba(0,0,0,.34)" } },
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: { minHeight: 44, borderRadius: 6, paddingInline: 20 },
            contained: { boxShadow: "inset 0 1px rgba(255,255,255,.16)", "&:hover": { boxShadow: `0 10px 28px ${alpha(cyan, .16)}` } },
            outlined: { borderColor: "rgba(255,255,255,.24)", "&:hover": { borderColor: cyan, background: alpha(cyan, .07) } },
          },
        },
        MuiChip: {
          styleOverrides: { root: { minHeight: 32, borderRadius: 5, fontWeight: 750, letterSpacing: ".025em" } },
        },
        MuiTabs: { styleOverrides: { root: { minHeight: 48 }, indicator: { height: 3, borderRadius: 3 } } },
        MuiTab: { styleOverrides: { root: { minHeight: 48, textTransform: "none", fontWeight: 750, minWidth: 0 } } },
        MuiTableContainer: { styleOverrides: { root: { borderRadius: 8 } } },
        MuiTableCell: {
          styleOverrides: {
            root: { borderColor: "rgba(255,255,255,.07)", whiteSpace: "nowrap" },
            head: { color: "#C7D0DB", fontSize: ".72rem", fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" },
          },
        },
        MuiDrawer: { styleOverrides: { paper: { backgroundColor: "#080C13", backgroundImage: "none", borderInlineStart: "1px solid rgba(255,255,255,.1)" } } },
      },
    }), []);
}
